import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import DateRangeDisplay from '../components/DateRangeDisplay';
import SystemPayablesSummary from '../components/SystemPayablesSummary';
import { ledgerAPI, transactionAPI, reminderAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiHome, FiTrendingUp, FiTrendingDown, FiBook, FiBell, FiCalendar, FiClock, FiAlertCircle } from 'react-icons/fi';
import { format, startOfMonth, endOfMonth, subMonths, startOfWeek, endOfWeek, isToday, isYesterday } from 'date-fns';
import { formatAmount, formatSignedAmount } from '../utils/currency';
import { loadPrefs, savePrefs, autoLayout } from '../utils/layoutPrefs';
import { useTone } from '../contexts/ToneContext';
import './Dashboard.css';

const DETAILED_VIEW_KEY = 'hisab_detailed_txn_view';

const isIncomeType = (type) => type === 'income' || type === 'income_received';
const isNeutralType = (type) => ['payable', 'receivable', 'internal_transfer', 'scheduled'].includes(type);

// Category icon map for modern view
const getCategoryIcon = (categoryName, txnType) => {
  if (!categoryName) {
    if (txnType === 'self_transfer' || txnType === 'internal_transfer') return '↔';
    if (txnType === 'payable') return '◯';
    if (txnType === 'receivable') return '◎';
    if (txnType === 'scheduled') return '⏰';
    return '💰';
  }
  const name = categoryName.toLowerCase();
  if (name.includes('food') || name.includes('grocer') || name.includes('kirana')) return '🍔';
  if (name.includes('transport') || name.includes('auto') || name.includes('cab') || name.includes('petrol') || name.includes('fuel')) return '🚗';
  if (name.includes('shopping') || name.includes('cloth')) return '🛍';
  if (name.includes('rent') || name.includes('house') || name.includes('home')) return '🏠';
  if (name.includes('medical') || name.includes('health') || name.includes('doctor')) return '💊';
  if (name.includes('bill') || name.includes('electric') || name.includes('recharge') || name.includes('phone')) return '⚡';
  if (name.includes('education') || name.includes('school') || name.includes('book')) return '📚';
  if (name.includes('entertainment') || name.includes('movie')) return '🎬';
  if (name.includes('salary') || name.includes('income') || name.includes('freelance')) return '💵';
  if (name.includes('travel') || name.includes('hotel')) return '✈';
  if (name.includes('insurance')) return '🛡';
  if (name.includes('invest')) return '📈';
  if (name.includes('gift')) return '🎁';
  if (name.includes('subscription') || name.includes('emi')) return '🔄';
  if (name.includes('vendor') || name.includes('payment')) return '🏪';
  if (name.includes('transfer')) return '↔';
  if (isIncomeType(txnType)) return '💵';
  return '💰';
};

const formatSmartDate = (dateStr) => {
  try {
    const d = new Date(dateStr);
    if (isToday(d)) return 'Today';
    if (isYesterday(d)) return 'Yesterday';
    return format(d, 'dd MMM');
  } catch {
    return dateStr;
  }
};

const Dashboard = () => {
  const { t, toneEnabled } = useTone();
  const [ledgers, setLedgers] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [summary, setSummary] = useState({ income: 0, expense: 0, balance: 0, payables: 0, receivables: 0 });
  const [loading, setLoading] = useState(true);
  const [detailedView, setDetailedView] = useState(() => localStorage.getItem(DETAILED_VIEW_KEY) === 'true');
  const [viewPrefs, setViewPrefs] = useState(() => {
    const prefs = loadPrefs();
    if (!prefs._manual) prefs.layout = autoLayout();
    return prefs;
  });
  const [expandedTxnId, setExpandedTxnId] = useState(null);
  const [dateFilter, setDateFilter] = useState('this_month');

  const isGrid = viewPrefs.layout === 'grid';
  const isCompactDensity = viewPrefs.density === 'compact';

  // Date range for dashboard based on filter
  const dateRange = useMemo(() => {
    const now = new Date();
    switch (dateFilter) {
      case 'today':
        return { start: now, end: now };
      case 'this_week':
        return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) };
      case 'this_month':
        return { start: startOfMonth(now), end: endOfMonth(now) };
      case 'last_month': {
        const lm = subMonths(now, 1);
        return { start: startOfMonth(lm), end: endOfMonth(lm) };
      }
      default:
        return { start: startOfMonth(now), end: endOfMonth(now) };
    }
  }, [dateFilter]);

  useEffect(() => {
    fetchDashboardData();
  }, [dateFilter]);

  const fetchDashboardData = async () => {
    try {
      const dateParams = {};
      if (dateRange.start) dateParams.start_date = format(dateRange.start, 'yyyy-MM-dd');
      if (dateRange.end) dateParams.end_date = format(dateRange.end, 'yyyy-MM-dd');

      const [ledgersRes, transactionsRes, remindersRes, summaryRes] = await Promise.allSettled([
        ledgerAPI.getAll(),
        transactionAPI.getAll({ limit: 5, ...dateParams }),
        reminderAPI.getUpcoming({ days: 7 }),
        transactionAPI.getSummary(dateParams),
      ]);

      if (ledgersRes.status === 'fulfilled' && ledgersRes.value?.data?.data?.ledgers) {
        setLedgers(ledgersRes.value.data.data.ledgers);
      }

      if (transactionsRes.status === 'fulfilled' && transactionsRes.value?.data?.data?.transactions) {
        setRecentTransactions(transactionsRes.value.data.data.transactions);
      }

      if (remindersRes.status === 'fulfilled' && remindersRes.value?.data?.data?.reminders) {
        setUpcomingReminders(remindersRes.value.data.data.reminders);
      }

      if (summaryRes.status === 'fulfilled' && summaryRes.value?.data?.data?.summary) {
        setSummary(summaryRes.value.data.data.summary);
      }

      const allFailed = [ledgersRes, transactionsRes, remindersRes, summaryRes].every(
        res => res.status === 'rejected'
      );

      if (allFailed) {
        toast.error('Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Dashboard error:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner message="Loading dashboard..." />
      </Layout>
    );
  }

  const renderModernTransaction = (txn) => {
    const income = isIncomeType(txn.transaction_type);
    const neutral = isNeutralType(txn.transaction_type);
    const isVendorEntry = !!txn.vendor_id;
    const categoryName = txn.category_name || (isVendorEntry ? (toneEnabled ? 'Vendor Lenden' : 'Vendor Payment') : 'Uncategorized');
    const icon = getCategoryIcon(categoryName, txn.transaction_type);
    const amountColor = neutral ? 'var(--color-purple)' : (income ? 'var(--color-primary)' : 'var(--color-red)');

    return (
      <div key={txn.id} className="modern-txn-row">
        <div className="modern-txn-icon">{icon}</div>
        <div className="modern-txn-info">
          <span className="modern-txn-name">{categoryName}</span>
          <span className="modern-txn-meta">
            {formatSmartDate(txn.transaction_date)}
            {txn.description && ` · ${txn.description}`}
          </span>
        </div>
        <div className="modern-txn-amount" style={{ color: amountColor }}>
          {neutral ? '' : (income ? '+' : '−')}
          {formatAmount(txn.amount)}
        </div>
      </div>
    );
  };

  const renderCompactTransaction = (txn) => {
    const income = isIncomeType(txn.transaction_type);
    const neutral = isNeutralType(txn.transaction_type);
    const isVendorEntry = !!txn.vendor_id;
    const categoryName = txn.category_name || (isVendorEntry ? (toneEnabled ? 'Vendor Lenden' : 'Vendor Payment') : 'Uncategorized');
    const icon = getCategoryIcon(categoryName, txn.transaction_type);
    const amountColor = neutral ? 'var(--color-purple)' : (income ? 'var(--color-primary)' : 'var(--color-red)');
    const isExpanded = expandedTxnId === txn.id;

    return (
      <div
        key={txn.id}
        className={`modern-txn-row txn-card-compact${isExpanded ? ' txn-card-expanded' : ''}`}
        onClick={() => setExpandedTxnId(isExpanded ? null : txn.id)}
      >
        <div className="modern-txn-icon">{icon}</div>
        <div className="modern-txn-info">
          <span className="modern-txn-name">{categoryName}</span>
          <span className="modern-txn-meta">
            {formatSmartDate(txn.transaction_date)}
          </span>
        </div>
        <div className="modern-txn-amount" style={{ color: amountColor }}>
          {neutral ? '' : (income ? '+' : '−')}
          {formatAmount(txn.amount)}
        </div>
        {isExpanded && (
          <div className="txn-expanded-details">
            {txn.description && <span className="txn-expanded-desc">{txn.description}</span>}
            <span className="txn-expanded-date">{formatSmartDate(txn.transaction_date)}</span>
          </div>
        )}
      </div>
    );
  };

  const renderDetailedTransaction = (txn) => {
    const income = isIncomeType(txn.transaction_type);
    const neutral = isNeutralType(txn.transaction_type);
    const typeClass = neutral ? 'neutral' : (income ? 'income' : 'expense');
    const isVendorEntry = !!txn.vendor_id;
    const categoryName = txn.category_name || (isVendorEntry ? (toneEnabled ? 'Vendor Lenden' : 'Vendor Payment') : 'Uncategorized');
    let displayTitle = categoryName;
    if (txn.transaction_type === 'payable') {
      displayTitle = `${categoryName} - ${t('dashboard.payable')}`;
    } else if (txn.transaction_type === 'receivable') {
      displayTitle = `${categoryName} - ${t('dashboard.receivable')}`;
    }

    return (
      <div key={txn.id} className={`transaction-item ${typeClass}`}>
        <div>
          <h4>{displayTitle}</h4>
          <p>{txn.description}</p>
          <span className="date">
            <FiCalendar size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
            {formatDate(txn.transaction_date)}
          </span>
        </div>
        <div className="amount">
          {neutral ? formatAmount(txn.amount) : formatSignedAmount(txn.amount, income ? 'income' : 'expense')}
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="dashboard">
        <div className="dash-header">
          <h1><FiHome style={{ marginRight: 8, verticalAlign: 'middle' }} />{t('dashboard.title')}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="select-input"
              style={{ padding: '6px 10px', fontSize: '0.82rem', borderRadius: 6 }}
            >
              <option value="today">{toneEnabled ? 'Aaj' : 'Today'}</option>
              <option value="this_week">{toneEnabled ? 'Is Hafte' : 'This Week'}</option>
              <option value="this_month">{toneEnabled ? 'Is Mahine' : 'This Month'}</option>
              <option value="last_month">{toneEnabled ? 'Pichle Mahine' : 'Last Month'}</option>
            </select>
            <DateRangeDisplay startDate={dateRange.start} endDate={dateRange.end} toneEnabled={toneEnabled} />
          </div>
        </div>

        <div className="summary-cards">
          <div className="summary-card income">
            <h3><FiTrendingUp style={{ marginRight: 4, verticalAlign: 'middle' }} />{t('dashboard.total_income')}</h3>
            <p className="amount">{formatAmount(summary.income)}</p>
          </div>
          <div className="summary-card expense">
            <h3><FiTrendingDown style={{ marginRight: 4, verticalAlign: 'middle' }} />{t('dashboard.total_expense')}</h3>
            <p className="amount">{formatAmount(summary.expense)}</p>
          </div>
          <div className="summary-card balance">
            <h3><FiBook style={{ marginRight: 4, verticalAlign: 'middle' }} />{t('dashboard.balance')}</h3>
            <p className="amount">{formatAmount(summary.balance)}</p>
          </div>
        </div>

        {/* VENDOR ACCOUNTING: Derived views only */}
        {(summary.payables > 0 || summary.receivables > 0) && (() => {
          const netUdhar = summary.net_udhar ?? (summary.receivables - summary.payables);
          const netColor = netUdhar > 0 ? 'var(--color-primary)' : netUdhar < 0 ? 'var(--color-orange)' : 'var(--color-text-secondary)';
          return (
            <div className="summary-cards">
              {summary.payables > 0 && (
                <div className="summary-card" style={{ borderLeft: '4px solid var(--color-orange)' }}>
                  <h3 style={{ color: 'var(--color-orange)' }}><FiAlertCircle style={{ marginRight: 4, verticalAlign: 'middle' }} />{t('dashboard.payable')}</h3>
                  <p className="amount" style={{ color: 'var(--color-orange)' }}>{formatAmount(summary.payables)}</p>
                  <small className="dash-card-sub">{toneEnabled ? 'Vendors ko dena baaki' : 'Pending payments to vendors'}</small>
                </div>
              )}
              {summary.receivables > 0 && (
                <div className="summary-card" style={{ borderLeft: '4px solid var(--color-blue)' }}>
                  <h3 style={{ color: 'var(--color-blue)' }}><FiClock style={{ marginRight: 4, verticalAlign: 'middle' }} />{t('dashboard.receivable')}</h3>
                  <p className="amount" style={{ color: 'var(--color-blue)' }}>{formatAmount(summary.receivables)}</p>
                  <small className="dash-card-sub">{toneEnabled ? 'Vendors se lena baaki' : 'Expected from vendors'}</small>
                </div>
              )}
              <div className="summary-card" style={{ borderLeft: `4px solid ${netColor}` }}>
                <h3 style={{ color: netColor }}>
                  {toneEnabled ? 'Net Udhar' : 'Net Outstanding'}
                </h3>
                <p className="amount" style={{ color: netColor }}>
                  {netUdhar > 0 ? `+${formatAmount(netUdhar)}` : netUdhar < 0 ? `-${formatAmount(Math.abs(netUdhar))}` : formatAmount(0)}
                </p>
                <small className="dash-card-sub">
                  {netUdhar > 0 ? (toneEnabled ? 'Vendors se lena hai' : 'Vendors owe you') :
                   netUdhar < 0 ? (toneEnabled ? 'Vendors ko dena hai' : 'You owe vendors') :
                   (toneEnabled ? 'Hisab barabar' : 'Settled')}
                </small>
              </div>
            </div>
          );
        })()}

        <SystemPayablesSummary />

        <div className="dashboard-grid">
          <div className="card">
            <h2><FiBook style={{ marginRight: 8, verticalAlign: 'middle' }} />{t('dashboard.your_ledgers')}</h2>
            {ledgers.length > 0 ? (
              <div className="ledger-list">
                {ledgers.map((ledger) => (
                  <div key={ledger.id} className="ledger-item" style={{ borderLeftColor: ledger.color }}>
                    <div>
                      <h4>{ledger.name}</h4>
                      <p>{ledger.description}</p>
                    </div>
                    <div className="ledger-balance">
                      <span className="label">{t('ledger.balance')}:</span>
                      <span className="value">{formatAmount(ledger.balance)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={<FiBook />}
                title={t('dashboard.no_ledgers')}
                message={t('dashboard.create_ledger_msg')}
              />
            )}
          </div>

          <div className="card">
            <div className="dash-section-header">
              <h2><FiTrendingDown style={{ marginRight: 8, verticalAlign: 'middle' }} />{t('dashboard.recent_transactions')}</h2>
              <div style={{ display: 'flex', gap: 4 }}>
                {!detailedView && (
                  <button
                    className="dash-view-toggle"
                    onClick={() => {
                      const nextLayout = isGrid ? 'list' : 'grid';
                      const nextDensity = nextLayout === 'grid' ? 'compact' : 'normal';
                      setViewPrefs(savePrefs({ layout: nextLayout, density: nextDensity }));
                      setExpandedTxnId(null);
                    }}
                    title={isGrid ? 'Switch to list layout' : 'Switch to grid layout'}
                  >
                    {isGrid ? '☰' : '⊞'}
                  </button>
                )}
                <button
                  className="dash-view-toggle"
                  onClick={() => {
                    const next = !detailedView;
                    setDetailedView(next);
                    localStorage.setItem(DETAILED_VIEW_KEY, next.toString());
                  }}
                  title={detailedView ? 'Switch to modern view' : 'Switch to detailed view'}
                >
                  {detailedView ? 'Modern' : 'Detailed'}
                </button>
              </div>
            </div>
            {recentTransactions.length > 0 ? (
              detailedView ? (
                <div className="transaction-list">
                  {recentTransactions.map(renderDetailedTransaction)}
                </div>
              ) : (
                <div className={`modern-txn-list${isGrid ? ' txn-grid' : ''}${isCompactDensity ? ' txn-density-compact' : ' txn-density-normal'}`}>
                  {recentTransactions.map(isGrid ? renderCompactTransaction : renderModernTransaction)}
                </div>
              )
            ) : (
              <EmptyState
                icon={<FiTrendingDown />}
                title={t('dashboard.no_transactions')}
                message={t('dashboard.add_transaction_msg')}
              />
            )}
          </div>

          <div className="card">
            <h2><FiBell style={{ marginRight: 8, verticalAlign: 'middle' }} />{t('dashboard.upcoming_reminders')}</h2>
            {upcomingReminders.length > 0 ? (
              <div className="reminder-list">
                {upcomingReminders.map((reminder) => (
                  <div key={reminder.id} className="reminder-item">
                    <h4>{reminder.title}</h4>
                    <p>{reminder.description}</p>
                    <div className="reminder-date">
                      <FiCalendar size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      {formatDate(reminder.reminder_date)}
                      {reminder.reminder_time && ` at ${reminder.reminder_time}`}
                    </div>
                    {reminder.amount && <div className="reminder-amount">{formatAmount(reminder.amount)}</div>}
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={<FiBell />}
                title={t('dashboard.no_reminders')}
                message={t('dashboard.all_caught_up')}
              />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
